/**
 * KioskWallPage · v2.7.60+E43 (2026-07-03) — Video Wall Overhaul
 * ═════════════════════════════════════════════════════════════════════
 * Public kiosk dispatch wall. Pulls the singleton config from
 *   GET /api/public/kiosk-wall/config
 * and renders the grid using percentage / aspect-ratio CSS so the same
 * URL drives 1080p AND 4K wall-mounted displays flawlessly.
 *
 * v2.7.60+E43 (Overhaul):
 *   • URL param plumbing: ?token= (long-lived), ?res=1080p|4k,
 *     ?hw=macos|chromeos|raspi|androidtv, ?zone=Z1..Z7. All cells
 *     receive the active zone via KioskZoneContext.
 *   • New cell renderers: zoned_live_map (§1), dispatch_board_columns
 *     (§2), fleet_stream (§3), warnings_top10 (§5).
 *   • Fixed supervisor command footer (§8) — high-contrast bottom-center
 *     bar, layered above all cells, driven by /api/public/kiosk-wall
 *     /supervisor.
 *   • Memory-safe: no autoplay video, ImageMapType overlays disposed
 *     before recreation, animation loops throttled + cleared on unmount.
 *     Target <250MB steady-state RAM on Pi 4 kiosk hardware.
 */
import { Component, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { NWS_INFO } from "@/lib/nwsColors";

const API = process.env.REACT_APP_BACKEND_URL;

const GRID_DIMS = {
  "1x1": { cols: 1, rows: 1 },
  "2x1": { cols: 2, rows: 1 },
  "2x2": { cols: 2, rows: 2 },
  "3x2": { cols: 3, rows: 2 },
  "3x3": { cols: 3, rows: 3 },
  "4x2": { cols: 4, rows: 2 },
};

// ─────────────────────────────────────────────────────────────────────
// URL parameter helpers (§6 · Platform Deployment provisioning).
// The kiosk auto-reads its target profile from the URL so the same
// hardware config can be re-pointed by simply changing the query string.
// ─────────────────────────────────────────────────────────────────────
function readKioskParams() {
  if (typeof window === "undefined") return {};
  const q = new URLSearchParams(window.location.search);
  const raw = {
    token: (q.get("token") || "").trim(),
    res:   (q.get("res")   || "1080p").toLowerCase(),
    hw:    (q.get("hw")    || "").toLowerCase(),
    zone:  (q.get("zone")  || "").toUpperCase(),
    // v2.7.62+E45 · which wall config to load (multi-instance topology)
    wall:  (q.get("wall")  || "").trim(),
  };
  // Snap res + hw to known values so the rest of the app can dispatch on them.
  if (!["1080p", "4k"].includes(raw.res)) raw.res = "1080p";
  if (!["macos", "chromeos", "raspi", "androidtv", ""].includes(raw.hw)) raw.hw = "";
  return raw;
}

// Broadcast the current selected zone to every cell (dispatch columns,
// live map, fleet stream). The header dropdown updates this; cells that
// don't care simply ignore it.
// v2.7.62+E45 · Zone SELECTION is now PER-CELL (each ZonedLiveMap has its
// own dropdown, up to 7 independent instances). Context only carries the
// shared zone CATALOGUE + perf profile + URL params — no active zone.
const KioskZoneContext = createContext({ zones: [], perf: null, params: {} });
export const useKioskZone = () => useContext(KioskZoneContext);

// Hardware-profile → performance tuning. Raspberry Pi + Android TV get
// throttled animation loops so they don't crash the browser process on
// long 24/7 runs (§6 hardware flag optimization).
function hwPerfProfile(hw) {
  switch (hw) {
    case "raspi":
      return { radarFrameMs: 1600, dispatchPollMs: 15000, fleetPollMs: 20000, mapMaxZoom: 12 };
    case "androidtv":
      return { radarFrameMs: 1200, dispatchPollMs: 12000, fleetPollMs: 15000, mapMaxZoom: 13 };
    case "macos":
    case "chromeos":
    default:
      return { radarFrameMs: 700, dispatchPollMs: 8000, fleetPollMs: 10000, mapMaxZoom: 16 };
  }
}

export default function KioskWallPage() {
  const [cfg, setCfg] = useState(null);
  const [now, setNow] = useState(new Date());
  const [tokenState, setTokenState] = useState({ status: "checking", profile: null });
  const [supervisor, setSupervisor] = useState(null);
  const [zones, setZones] = useState([]);

  // Read URL params ONCE per mount (kiosk hardware doesn't navigate).
  const params = useMemo(() => readKioskParams(), []);
  const perf = useMemo(() => hwPerfProfile(params.hw), [params.hw]);

  // v2.7.62+E45 · multi-instance topology. Wall_id resolves in priority:
  //   1) explicit ?wall=<id> URL param
  //   2) token profile's wall_id (set at provisioning time)
  //   3) "default" singleton
  const [wallId, setWallId] = useState(params.wall || "default");

  // Token validation — if provided, MUST resolve before rendering the
  // wall (§6 access-token model). No token → public/anonymous mode.
  // v2.7.62+E45 · a resolved token may carry a `wall_id` that pins this
  // physical device to a specific isolated wall config.
  useEffect(() => {
    let cancelled = false;
    if (!params.token) { setTokenState({ status: "anonymous", profile: null }); return; }
    axios.get(`${API}/api/public/kiosk-wall/token/${encodeURIComponent(params.token)}`)
      .then((r) => {
        if (cancelled) return;
        setTokenState({ status: "ok", profile: r.data });
        // Prefer the token's wall_id ONLY if the URL didn't already pin one.
        if (!params.wall && r.data?.wall_id) setWallId(r.data.wall_id);
      })
      .catch(() => { if (!cancelled) setTokenState({ status: "revoked", profile: null }); });
    return () => { cancelled = true; };
  }, [params.token, params.wall]);

  // Initial config + 30s refresh — v2.7.62+E45 · loads the specific wall_id's
  // isolated config (backend falls back to the "default" singleton when the
  // id doesn't exist).
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/config`, { params: { wall_id: wallId } })
      .then((r) => setCfg(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [wallId]);

  // Zones registry — loaded once, referenced by header dropdown + cells.
  useEffect(() => {
    axios.get(`${API}/api/public/kiosk-wall/zones`).then((r) => setZones(r.data?.zones || [])).catch(() => {});
  }, []);

  // Supervisor (footer) — refresh every 60s so a shift change reflects
  // on all walls without human intervention.
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/supervisor`).then((r) => setSupervisor(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  // Clock tick
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // If a token was passed and validation failed → refuse to render. Wall
  // hardware becomes "REVOKED" so ops sees the state at a glance.
  if (tokenState.status === "revoked") {
    return (
      <div data-testid="kiosk-wall-revoked" style={{ background: "#450a0a", color: "#fff", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "2.4vw", fontWeight: 900, letterSpacing: "0.15em", textTransform: "uppercase", color: "#fecaca" }}>Access Revoked</div>
          <div style={{ marginTop: "0.8vw", color: "#fca5a5", fontSize: "1vw" }}>This kiosk token is no longer active. Contact dispatch.</div>
        </div>
      </div>
    );
  }

  if (!cfg || tokenState.status === "checking") {
    return (
      <div style={{ background: "#000", color: "#fff", height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase" }}>Buller Taxi · Dispatch Wall</div>
          <div style={{ marginTop: 8, color: "#888" }}>Loading kiosk configuration…</div>
        </div>
      </div>
    );
  }

  // 4K flag → bump root font-size so every vw/vh sized element scales
  // predictably (§6 res=4k / res=1080p toggle).
  const rootStyle = {
    background: "#000",
    color: "#fff",
    width: "100vw",
    height: "100vh",
    overflow: "hidden",
    fontFamily: "system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    fontSize: params.res === "4k" ? "20px" : "14px",
  };

  // The current zone object (or nothing) — used for header label.
  const activeZone = null;  // v2.7.62+E45 · no global zone; per-cell only

  return (
    <KioskZoneContext.Provider value={{ zones, perf, params }}>
      <div data-testid="kiosk-wall-root" data-hw={params.hw || "generic"} data-res={params.res} data-wall-id={wallId} style={rootStyle}>
        {/* Header strip · title + wall_id + clock (v2.7.62+E45: no global zone selector) */}
        <div style={{ flex: "0 0 auto", padding: "0.5vw 1vw", borderBottom: "1px solid #222", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1vw" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1.2vw", minWidth: 0 }}>
            <div style={{ fontSize: "1.2vw", fontWeight: 900, letterSpacing: "0.1em", textTransform: "uppercase", color: "#fbbf24", whiteSpace: "nowrap" }}>
              {cfg.title}
            </div>
            {wallId && wallId !== "default" && (
              <span data-testid="wall-id-badge" style={{ fontSize: "0.7vw", padding: "0.2vw 0.5vw", background: "#1e293b", color: "#60a5fa", borderRadius: "0.2vw", fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase" }}>Wall · {wallId}</span>
            )}
          </div>
          {cfg.show_clock && (
            <div style={{ fontSize: "1.4vw", fontWeight: 800, fontVariantNumeric: "tabular-nums" }} data-testid="kiosk-clock">
              {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              <span style={{ color: "#666", marginLeft: "0.8vw", fontSize: "0.9vw" }}>{now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</span>
            </div>
          )}
        </div>

        {/* Grid body — v2.7.62+E45 · dispatches to the new 55-45-quad
             topology renderer OR the legacy NxN grid renderer. */}
        {cfg.grid === "55-45-quad"
          ? <QuadTopologyBody cells={cfg.cells || []}/>
          : <LegacyGridBody grid={cfg.grid} cells={cfg.cells || []}/>}

        {/* Optional weather strip footer (legacy) */}
        {cfg.show_weather_strip && <WeatherStrip/>}

        {/* §8 · Fixed Supervisor Command Footer — bottom-center, high-contrast */}
        <SupervisorFooter supervisor={supervisor}/>
      </div>
    </KioskZoneContext.Provider>
  );
}

// v2.7.62+E45 · Legacy NxN grid renderer — preserves every kiosk config
// that pre-dates the new 55-45-quad topology.
function LegacyGridBody({ grid, cells }) {
  const dims = GRID_DIMS[grid] || GRID_DIMS["2x2"];
  return (
    <div
      data-testid={`kiosk-wall-grid-${grid || "2x2"}`}
      style={{
        flex: "1 1 auto",
        display: "grid",
        gridTemplateColumns: `repeat(${dims.cols}, 1fr)`,
        gridTemplateRows: `repeat(${dims.rows}, 1fr)`,
        gap: "0.4vw",
        padding: "0.4vw",
        paddingBottom: "3.2vw",
      }}
    >
      {cells.map((c, idx) => <Cell key={c.id || idx} cell={c} index={idx}/>)}
    </div>
  );
}

// v2.7.62+E45 · §3 · 4K-optimized 55/45 topology renderer.
//   Upper 55% (fr 55): 3-column layout —
//     • Left 2 cols (2x2 grid) = 4 map slots (cells slot_maps[0..3])
//     • Right col split vertically: radar (top), warnings (bottom)
//   Lower 45% (fr 45): 3-column layout —
//     • Left col:  dispatch_active_booked_future  (Active / Booked / Future 72h stack)
//     • Middle col: fleet_stream  (single-line-per-vehicle)
//     • Right col: dispatch_completed  (last 20)
//
// Cells are addressed by their `slot` field (map_1..map_4, radar, warnings,
// abf, fleet, completed). Missing slots render as an empty placeholder so
// admins can wire them one at a time.
function QuadTopologyBody({ cells }) {
  const bySlot = {};
  cells.forEach((c) => { if (c.slot) bySlot[c.slot] = c; });
  const renderSlot = (slot, fallbackType) => {
    const c = bySlot[slot] || { id: `slot-${slot}`, type: fallbackType, slot };
    return <Cell cell={c} index={slot}/>;
  };
  return (
    <div
      data-testid="kiosk-wall-grid-55-45-quad"
      style={{
        flex: "1 1 auto",
        display: "grid",
        gridTemplateRows: "55fr 45fr",
        gap: "0.4vw",
        padding: "0.4vw",
        paddingBottom: "3.2vw",
        minHeight: 0,
      }}
    >
      {/* Upper 55% */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "0.4vw", minHeight: 0 }} data-testid="quad-upper">
        {/* 2x2 map quadrant on the left */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: "0.4vw", minHeight: 0 }} data-testid="quad-maps-2x2">
          {["map_1", "map_2", "map_3", "map_4"].map((s) => (
            <div key={s} data-testid={`quad-slot-${s}`} style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>
              {renderSlot(s, "zoned_live_map")}
            </div>
          ))}
        </div>
        {/* Radar + Warnings stacked on the right */}
        <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: "0.4vw", minHeight: 0 }} data-testid="quad-right-column">
          <div data-testid="quad-slot-radar" style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>{renderSlot("radar", "weather_radar")}</div>
          <div data-testid="quad-slot-warnings" style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>{renderSlot("warnings", "warnings_top10")}</div>
        </div>
      </div>
      {/* Lower 45% */}
      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 0.9fr 1.2fr", gap: "0.4vw", minHeight: 0 }} data-testid="quad-lower">
        <div data-testid="quad-slot-abf"       style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>{renderSlot("abf",       "dispatch_active_booked_future")}</div>
        <div data-testid="quad-slot-fleet"     style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>{renderSlot("fleet",     "fleet_stream")}</div>
        <div data-testid="quad-slot-completed" style={{ minHeight: 0, minWidth: 0, position: "relative", display: "flex", flexDirection: "column" }}>{renderSlot("completed", "dispatch_completed")}</div>
      </div>
    </div>
  );
}


// v2.7.62+E45 · Legacy top-level ZoneSelector component removed —
// per-cell zone dropdowns replace it (see ZonedLiveMapCell below).


// ─────────────────────────────────────────────────────────────────────
// §8 · Supervisor Command Footer — fixed to viewport bottom-center,
// layered above the grid. Exact string format:
//   SUPERVISOR: [FIRST] [LAST] | [PHONE]
// ─────────────────────────────────────────────────────────────────────
function SupervisorFooter({ supervisor }) {
  const s = supervisor || {};
  const name = `${s.first_name || "—"} ${s.last_name || ""}`.trim();
  const phone = s.phone || "—";
  return (
    <div
      data-testid="kiosk-supervisor-footer"
      style={{
        position: "fixed",
        left: "50%",
        bottom: "0.4vw",
        transform: "translateX(-50%)",
        background: "linear-gradient(180deg, #b91c1c 0%, #7f1d1d 100%)",
        color: "#fff",
        padding: "0.5vw 1.6vw",
        borderRadius: "0.5vw",
        boxShadow: "0 0.4vw 1vw rgba(0,0,0,0.6)",
        fontWeight: 900,
        fontSize: "1.1vw",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        border: "0.15vw solid #fbbf24",
        zIndex: 9999,
        pointerEvents: "none",
        whiteSpace: "nowrap",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <span style={{ color: "#fcd34d", marginRight: "0.6vw" }}>SUPERVISOR:</span>
      <span data-testid="kiosk-supervisor-name">{name}</span>
      <span style={{ color: "#fbbf24", margin: "0 0.5vw" }}>|</span>
      <span data-testid="kiosk-supervisor-phone">{phone}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Cell · wraps the chosen renderer in a per-cell ErrorBoundary so one
// broken cell can't take down the entire wall.
// ─────────────────────────────────────────────────────────────────────
function Cell({ cell, index }) {
  const type = cell?.type || "blank";
  const Renderer = CELL_RENDERERS[type] || UnknownCell;
  return (
    <div
      data-testid={`kiosk-wall-cell-${index}`}
      data-cell-type={type}
      style={{
        background: "#0b0b0d",
        border: "1px solid #1a1a1f",
        borderRadius: "0.5vw",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",   // v2.7.62+E45 · fill the parent grid/flex cell
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div style={{ flex: "0 0 auto", padding: "0.4vw 0.8vw", borderBottom: "1px solid #1a1a1f", fontSize: "0.7vw", textTransform: "uppercase", letterSpacing: "0.15em", color: "#888", fontWeight: 800 }}>
        {type.replace(/_/g, " ")}
      </div>
      <div style={{ flex: "1 1 auto", overflow: "hidden", position: "relative" }}>
        <CellErrorBoundary type={type}>
          <Renderer cell={cell}/>
        </CellErrorBoundary>
      </div>
    </div>
  );
}

class CellErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) {
     
    console.warn(`[kiosk-wall] ${this.props.type} cell crashed:`, err, info);
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "1vw", color: "#ef4444" }}>
          <div>
            <div style={{ fontSize: "1vw", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.1em" }}>Cell error</div>
            <div style={{ fontSize: "0.7vw", color: "#888", marginTop: "0.4vw" }}>{this.props.type} · check console</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Cell renderer registry — replaces the previous `switch`. Adding a
// new tile type ONLY requires registering a component here.
// ─────────────────────────────────────────────────────────────────────
const CELL_RENDERERS = {
  active_warnings:         ActiveWarningsCell,
  warnings_top10:          WarningsTop10Cell,
  earnings_board:          EarningsBoardCell,
  fleet_status:            FleetStatusCell,
  fleet_stream:            FleetStreamCell,
  forecast_12day:          Forecast12DayCell,
  state_emergencies:       StateEmergenciesCell,
  live_map:                LiveMapCell,
  zoned_live_map:          ZonedLiveMapCell,
  weather_radar:           WeatherRadarCell,
  dispatch_board:          DispatchBoardCell,
  dispatch_board_columns:  DispatchColumnsCell,
  // v2.7.62+E45 · §3 quad-topology variants of the dispatch board
  dispatch_active_booked_future: (props) => <DispatchColumnsCell {...props} cell={{ ...(props.cell || {}), variant: "abf" }}/>,
  dispatch_completed:            (props) => <DispatchColumnsCell {...props} cell={{ ...(props.cell || {}), variant: "completed" }}/>,
  completed_rides:         CompletedRidesCell,
  blank:                   BlankCell,
};

function UnknownCell({ cell }) {
  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "1vw" }}>
      <div>
        <div style={{ fontSize: "1vw", fontWeight: 900, color: "#fbbf24", textTransform: "uppercase", letterSpacing: "0.1em" }}>Unknown cell type</div>
        <div style={{ fontSize: "0.7vw", color: "#888", marginTop: "0.4vw" }}>{cell?.type || "—"}</div>
        <div style={{ fontSize: "0.65vw", color: "#666", marginTop: "0.3vw" }}>Register a renderer in CELL_RENDERERS</div>
      </div>
    </div>
  );
}

function BlankCell() {
  return (
    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "1vw" }}>
      <div>
        <div style={{ fontSize: "0.95vw", fontWeight: 900, color: "#e4e4e7", textTransform: "uppercase", letterSpacing: "0.08em" }}>Empty cell</div>
        <div style={{ fontSize: "0.65vw", color: "#666", marginTop: "0.4vw" }}>Configure in /admin/developer/dispatch-video-wall</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Live cells
// ─────────────────────────────────────────────────────────────────────
function ActiveWarningsCell() {
  const [alerts, setAlerts] = useState([]);
  useEffect(() => {
    const load = () => axios.get("https://api.weather.gov/alerts/active?area=OK", {
      headers: { "User-Agent": "BullerTaxi Kiosk Wall" },
    }).then((r) => setAlerts(r.data?.features || [])).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }}>
      <div style={{ fontSize: "2vw", fontWeight: 900, color: "#ef4444", fontVariantNumeric: "tabular-nums" }}>
        {alerts.length}
        <span style={{ fontSize: "0.8vw", color: "#888", marginLeft: "0.5vw", letterSpacing: "0.1em" }}>ACTIVE</span>
      </div>
      <div style={{ marginTop: "0.4vw", overflowY: "hidden", height: "calc(100% - 3vw)" }}>
        {alerts.slice(0, 12).map((a) => {
          const info = NWS_INFO(a.properties?.event, a.properties?.severity);
          return (
            <div key={a.id} style={{ borderLeft: `3px solid ${info.stroke}`, padding: "0.2vw 0.5vw", marginBottom: "0.2vw" }}>
              <div style={{ fontSize: "0.75vw", fontWeight: 900, color: info.stroke, textTransform: "uppercase", letterSpacing: "0.05em" }}>{a.properties?.event}</div>
              <div style={{ fontSize: "0.65vw", color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.properties?.headline || ""}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EarningsBoardCell() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/earnings-board`).then((r) => setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "1vw", gap: "0.6vw" }}>
      <div>
        <div style={{ fontSize: "0.7vw", color: "#888", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800 }}>Today</div>
        <div style={{ fontSize: "2.6vw", fontWeight: 900, color: "#fbbf24", fontVariantNumeric: "tabular-nums" }}>
          {data ? fmt(data.today?.total_cents) : "—"}
        </div>
        <div style={{ fontSize: "0.65vw", color: "#666" }}>{data?.today?.count || 0} rides</div>
      </div>
      <div style={{ height: "1px", width: "60%", background: "#1a1a1f" }}/>
      <div>
        <div style={{ fontSize: "0.7vw", color: "#888", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800 }}>Past 7 Days</div>
        <div style={{ fontSize: "1.6vw", fontWeight: 800, color: "#a7f3d0", fontVariantNumeric: "tabular-nums" }}>
          {data ? fmt(data.week?.total_cents) : "—"}
        </div>
        <div style={{ fontSize: "0.65vw", color: "#666" }}>{data?.week?.count || 0} rides</div>
      </div>
    </div>
  );
}

function FleetStatusCell() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/api/health`).then((r) => setStats(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", padding: "1vw" }}>
      <div style={{ fontSize: "0.7vw", color: "#888", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800 }}>System</div>
      <div style={{ fontSize: "1.6vw", fontWeight: 900, color: stats ? "#22c55e" : "#666" }}>{stats ? "ONLINE" : "—"}</div>
      <div style={{ fontSize: "0.7vw", color: "#666", marginTop: "0.4vw" }}>API health</div>
    </div>
  );
}

function Forecast12DayCell() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/public/local-weather/forecast?days=12`).then((r) => setRows(r.data?.rows || [])).catch(() => {});
  }, []);
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw", display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "repeat(2, 1fr)", gap: "0.3vw" }}>
      {rows.slice(0, 12).map((r) => (
        <div key={r.date} style={{ background: "#0f0f12", border: "1px solid #1a1a1f", borderRadius: "0.3vw", padding: "0.3vw", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ fontSize: "0.55vw", color: "#888", textTransform: "uppercase" }}>{new Date(r.date + "T12:00:00").toLocaleDateString([], { weekday: "short" })}</div>
          <div style={{ fontSize: "1vw", fontWeight: 900, color: "#e4e4e7" }}>{Math.round(r.temp_max_f)}°</div>
          <div style={{ fontSize: "0.55vw", color: "#888" }}>{Math.round(r.temp_min_f)}°</div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// State Emergencies · AMBER / Silver / Public-Safety filter
// ─────────────────────────────────────────────────────────────────────
function StateEmergenciesCell() {
  const [data, setData] = useState({ count: 0, items: [] });
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/state-emergencies`).then((r) => setData(r.data || { count: 0, items: [] })).catch(() => {});
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.5vw" }}>
        <div style={{ fontSize: "2vw", fontWeight: 900, color: data.count > 0 ? "#f97316" : "#22c55e", fontVariantNumeric: "tabular-nums" }}>{data.count}</div>
        <div style={{ fontSize: "0.7vw", color: "#888", letterSpacing: "0.1em", textTransform: "uppercase" }}>Public Safety</div>
      </div>
      <div style={{ marginTop: "0.4vw", overflow: "hidden", height: "calc(100% - 3vw)" }}>
        {data.count === 0 && (
          <div style={{ fontSize: "0.75vw", color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 800 }}>● No active AMBER / Silver alerts</div>
        )}
        {data.items.slice(0, 6).map((a) => (
          <div key={a.id} style={{ borderLeft: "3px solid #f97316", padding: "0.2vw 0.5vw", marginBottom: "0.25vw" }}>
            <div style={{ fontSize: "0.78vw", fontWeight: 900, color: "#f97316", textTransform: "uppercase", letterSpacing: "0.05em" }}>{a.event}</div>
            <div style={{ fontSize: "0.62vw", color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.headline}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Live Map cell · Google Maps tile showing dispatch zones. Live driver
// positions are NOT shown here (the kiosk is anonymously reachable and
// driver-location streaming requires a JWT). The "Dispatch Board" cell
// covers ride-volume visibility instead.
// ─────────────────────────────────────────────────────────────────────
function LiveMapCell() {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Lazy-import to avoid pulling Google Maps onto wall variants
        // that don't include this cell.
        const { default: loadGoogleMaps } = await import("@/lib/loadGoogleMaps");
        await loadGoogleMaps();
        if (cancelled || !ref.current) return;
        const map = new window.google.maps.Map(ref.current, {
          center: { lat: 35.4676, lng: -97.5164 }, // OKC default
          zoom: 10,
          disableDefaultUI: true,
          gestureHandling: "none",
          backgroundColor: "#000",
          styles: DARK_MAP_STYLES,
        });
        // Optional: drop a static marker on the dispatch HQ
         
        new window.google.maps.Marker({
          position: { lat: 35.4676, lng: -97.5164 },
          map,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: "#fbbf24",
            fillOpacity: 1,
            strokeColor: "#000",
            strokeWeight: 2,
          },
        });
        if (!cancelled) setLoaded(true);
      } catch (e) {
         
        console.warn("[LiveMapCell] map init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <div style={{ position: "relative", height: "100%" }}>
      <div ref={ref} style={{ position: "absolute", inset: 0 }} data-testid="kiosk-live-map"/>
      {!loaded && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.7vw", textTransform: "uppercase", letterSpacing: "0.1em" }}>Loading map…</div>
      )}
    </div>
  );
}

const DARK_MAP_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#0b0b0d" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0b0b0d" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7280" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#1f2937" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca3af" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0f172a" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
];

// ─────────────────────────────────────────────────────────────────────
// §4 · Weather Radar (v2.7.60+E43 overhaul) — RainViewer tiles overlaid
// on a Google Map, with interactive playback speed (0.5x/1x/2x/4x),
// play/pause, 60-min scrubber, and smooth pinch/wheel zoom. Chosen over
// direct NEXRAD Level III because RainViewer already provides the full
// 60-minute historical stack in ready-to-render tile form (§4 spec
// permits any Nexrad-derived Doppler layer over a statewide GIS base).
// ─────────────────────────────────────────────────────────────────────
function WeatherRadarCell() {
  const { perf } = useKioskZone();
  const ref = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);          // v2.7.62+E45 · array of stacked ImageMapTypes for offscreen pre-cache
  const dataLayerRef = useRef(null);
  const preloadCacheRef = useRef(new Set()); // track fully-loaded frame paths
  const [frames, setFrames] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [speedMultiplier, setSpeedMultiplier] = useState(1);
  const [polyCount, setPolyCount] = useState(0);
  const [readyFrames, setReadyFrames] = useState(0);
  const [mapReady, setMapReady] = useState(false);

  // 1) Poll RainViewer — grab the full 60min historical stack (past
  //    frames are 10min-spaced, so ~6 covers the last hour) + 3 nowcast
  //    projections.
  useEffect(() => {
    const load = () => axios.get("https://api.rainviewer.com/public/weather-maps.json").then((r) => {
      const past = r.data?.radar?.past || [];
      const nowcast = r.data?.radar?.nowcast || [];
      const host = r.data?.host || "https://tilecache.rainviewer.com";
      const all = [...past.slice(-6), ...nowcast.slice(0, 3)].map((f) => ({
        time: f.time,
        path: `${host}${f.path}`,
      }));
      setFrames(all);
    }).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // 2) Animate through frames. Base frame duration is hardware-tuned
  //    (perf profile), scaled by the user-selected speed multiplier.
  //    v2.7.62+E45 · SKIP frames that haven't fully preloaded — this
  //    is the "offscreen canvas pre-cache" that eliminates the flash /
  //    tearing when a partially-cached tile pyramid renders.
  useEffect(() => {
    if (!frames.length || !playing || readyFrames === 0) return undefined;
    const intervalMs = Math.max(120, perf.radarFrameMs / speedMultiplier);
    const id = setInterval(() => {
      setIdx((i) => {
        // Advance to the next READY frame (skip frames still loading)
        for (let step = 1; step <= frames.length; step += 1) {
          const cand = (i + step) % frames.length;
          if (preloadCacheRef.current.has(frames[cand].path)) return cand;
        }
        return i;
      });
    }, intervalMs);
    return () => clearInterval(id);
  }, [frames, playing, speedMultiplier, perf.radarFrameMs, readyFrames]);

  // 2b) v2.7.62+E45 · Offscreen prewarm — for each frame path, kick off
  //     an off-DOM Image() that loads the center OKC tile. When it's
  //     complete, we mark the whole frame as "ready to render". This
  //     lets the map's built-in tile fetcher land on already-warmed HTTP
  //     caches when the frame is inserted, eliminating tearing.
  useEffect(() => {
    if (!frames.length) return;
    // Roughly the center OKC tile at zoom 7:
    //   x = ~28, y = ~50 at z=7 (approximate — the browser will cache
    //   nearby tiles anyway thanks to CDN semi-key locality).
    const tileZoom = 7, tileX = 28, tileY = 50;
    frames.forEach((f) => {
      if (preloadCacheRef.current.has(f.path)) return;
      const img = new Image();
      img.decoding = "async";
      img.crossOrigin = "anonymous";
      img.onload = () => {
        preloadCacheRef.current.add(f.path);
        setReadyFrames(preloadCacheRef.current.size);
      };
      img.onerror = () => {
        // Mark ready anyway — a single missing tile shouldn't stall the loop
        preloadCacheRef.current.add(f.path);
        setReadyFrames(preloadCacheRef.current.size);
      };
      img.src = `${f.path}/256/${tileZoom}/${tileX}/${tileY}/2/1_1.png`;
    });
  }, [frames]);

  // 3) Initialise the map once — with §6 spec: greedy gestures + native
  //    zoomControl (+/- buttons) + scaleControl.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { default: loadGoogleMaps } = await import("@/lib/loadGoogleMaps");
        await loadGoogleMaps();
        if (cancelled || !ref.current) return;
        mapRef.current = new window.google.maps.Map(ref.current, {
          center: { lat: 35.4676, lng: -97.5164 },
          zoom: 7,
          minZoom: 4,
          maxZoom: perf.mapMaxZoom,
          disableDefaultUI: false,
          gestureHandling: "greedy",
          zoomControl: true,
          zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
          scaleControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          rotateControl: false,
          backgroundColor: "#000",
          styles: DARK_MAP_STYLES,
        });
        // Reusable Data layer for NWS polygons
        dataLayerRef.current = new window.google.maps.Data({ map: mapRef.current });
        dataLayerRef.current.setStyle((feature) => {
          const s = feature.getProperty("_style") || { stroke: "#94a3b8", fill: "#94a3b8", opacity: 0.2 };
          return {
            strokeColor: s.stroke,
            strokeOpacity: 0.9,
            strokeWeight: 2,
            fillColor: s.fill,
            fillOpacity: s.opacity,
            clickable: false,
            zIndex: 500,
          };
        });
        setMapReady(true);
      } catch (e) {
        console.warn("[WeatherRadarCell] map init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // 4) v2.7.62+E45 · Double-buffered radar overlay swap. We keep the
  //    PREVIOUS ImageMapType visible until AFTER we've inserted the
  //    new one, then remove the old one. This double-buffer strategy
  //    eliminates the black flash / tearing that plagued the single-
  //    overlay swap.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !frames.length || !window.google) return;
    const map = mapRef.current;
    const frame = frames[idx];
    if (!frame) return;
    // Only render frames we've prewarmed
    if (!preloadCacheRef.current.has(frame.path)) return;

    const layer = new window.google.maps.ImageMapType({
      getTileUrl: (coord, zoom) => `${frame.path}/256/${zoom}/${coord.x}/${coord.y}/2/1_1.png`,
      tileSize: new window.google.maps.Size(256, 256),
      opacity: 0.72,
      name: `radar-${frame.time}`,
    });

    // 4a) Insert NEW at the top of the stack
    map.overlayMapTypes.insertAt(overlaysRef.current.length, layer);
    overlaysRef.current.push(layer);

    // 4b) After a brief delay to let the new tiles paint, remove the
    //     stale layer beneath so we only ever have ≤ 2 overlays alive.
    //     (Guards against a leak if the effect churns.)
    const removeStale = setTimeout(() => {
      while (overlaysRef.current.length > 1) {
        overlaysRef.current.shift();
        try { map.overlayMapTypes.removeAt(0); } catch (_e) { /* ignore */ }
      }
    }, 220);

    return () => clearTimeout(removeStale);
  }, [idx, frames, mapReady]);

  // 5) v2.7.62+E45 · §6 · Live NWS Hazard GeoJSON polygon overlay.
  //    Poll every 30s, replace the Data layer content atomically.
  useEffect(() => {
    if (!mapReady || !dataLayerRef.current) return undefined;
    const layer = dataLayerRef.current;
    const load = () => {
      axios.get(`${API}/api/public/kiosk-wall/nws-polygons`).then((r) => {
        const fc = r.data;
        if (!fc || fc.type !== "FeatureCollection") return;
        // Atomic swap: wipe old, add new
        layer.forEach((f) => layer.remove(f));
        try {
          layer.addGeoJson(fc);
          setPolyCount(fc.count || (fc.features || []).length);
        } catch (e) {
          console.warn("[WeatherRadarCell] geojson parse failed", e);
        }
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 30 * 1000);
    return () => clearInterval(id);
  }, [mapReady]);

  const stamp = useMemo(() => {
    if (!frames.length) return "—";
    const t = frames[idx]?.time;
    if (!t) return "—";
    const d = new Date(t * 1000);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, [idx, frames]);

  const minutesAgo = useMemo(() => {
    if (!frames.length) return 0;
    const t = frames[idx]?.time;
    if (!t) return 0;
    const secs = Math.round((Date.now() / 1000 - t));
    return Math.round(secs / 60);
  }, [idx, frames]);

  const SPEEDS = [0.5, 1, 2, 4];

  return (
    <div style={{ position: "relative", height: "100%" }} data-testid="weather-radar-cell">
      <div ref={ref} style={{ position: "absolute", inset: 0, background: "#000" }} data-testid="kiosk-weather-radar"/>

      {/* Frame stamp + NWS polygon count */}
      <div style={{ position: "absolute", left: "0.5vw", top: "0.5vw", padding: "0.2vw 0.5vw", background: "rgba(0,0,0,0.7)", borderRadius: "0.2vw", fontSize: "0.6vw", color: "#fbbf24", fontWeight: 800, letterSpacing: "0.1em", display: "flex", gap: "0.5vw", alignItems: "center" }} data-testid="radar-timestamp">
        <span>RainViewer · {stamp}{minutesAgo > 0 ? ` (−${minutesAgo}m)` : ""}</span>
        <span style={{ color: "#94a3b8" }}>·</span>
        <span data-testid="radar-cache-count" style={{ color: readyFrames === frames.length && frames.length ? "#22c55e" : "#f59e0b" }}>{readyFrames}/{frames.length || 0} cached</span>
        {polyCount > 0 && (
          <>
            <span style={{ color: "#94a3b8" }}>·</span>
            <span data-testid="radar-nws-polygons" style={{ color: "#ef4444" }}>{polyCount} NWS polygon{polyCount === 1 ? "" : "s"}</span>
          </>
        )}
      </div>

      {/* Bottom controls: play/pause + speed + scrubber */}
      <div style={{
        position: "absolute", left: "0.5vw", right: "0.5vw", bottom: "0.5vw",
        background: "rgba(0,0,0,0.78)", borderRadius: "0.3vw", padding: "0.3vw 0.6vw",
        display: "flex", alignItems: "center", gap: "0.6vw", fontSize: "0.65vw",
        color: "#e4e4e7", pointerEvents: "auto",
      }} data-testid="radar-controls">
        <button
          data-testid="radar-play-pause"
          onClick={() => setPlaying((p) => !p)}
          style={{ background: playing ? "#22c55e" : "#f59e0b", color: "#000", border: 0, padding: "0.25vw 0.55vw", borderRadius: "0.2vw", fontWeight: 900, fontSize: "0.65vw", letterSpacing: "0.1em", cursor: "pointer", minWidth: "3vw" }}
        >{playing ? "PAUSE" : "PLAY"}</button>

        <div style={{ display: "flex", gap: "0.2vw" }}>
          {SPEEDS.map((s) => (
            <button
              key={s}
              data-testid={`radar-speed-${String(s).replace(".", "-")}x`}
              onClick={() => setSpeedMultiplier(s)}
              style={{
                background: speedMultiplier === s ? "#fbbf24" : "#111827",
                color: speedMultiplier === s ? "#000" : "#e4e4e7",
                border: "1px solid #374151",
                padding: "0.2vw 0.4vw", borderRadius: "0.2vw",
                fontWeight: 800, fontSize: "0.6vw", cursor: "pointer",
              }}
            >{s}×</button>
          ))}
        </div>

        <input
          type="range"
          data-testid="radar-scrubber"
          min={0}
          max={Math.max(0, frames.length - 1)}
          value={idx}
          onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }}
          style={{ flex: 1, accentColor: "#fbbf24" }}
        />
        <span data-testid="radar-frame-count" style={{ color: "#94a3b8", fontVariantNumeric: "tabular-nums", minWidth: "3vw", textAlign: "right" }}>
          {idx + 1}/{frames.length || 0}
        </span>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────
// Dispatch Board · live pending/active rides ticker
// ─────────────────────────────────────────────────────────────────────
function DispatchBoardCell() {
  const [data, setData] = useState({ count: 0, items: [] });
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/dispatch-board?limit=12`).then((r) => setData(r.data || { count: 0, items: [] })).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, []);
  const STATUS_COLOR = {
    pending: "#fbbf24",
    dispatching: "#3b82f6",
    assigned: "#a78bfa",
    en_route: "#22d3ee",
    on_trip: "#22c55e",
  };
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.5vw" }}>
        <div style={{ fontSize: "2vw", fontWeight: 900, color: "#22c55e", fontVariantNumeric: "tabular-nums" }}>{data.count}</div>
        <div style={{ fontSize: "0.7vw", color: "#888", textTransform: "uppercase", letterSpacing: "0.1em" }}>Active rides</div>
      </div>
      <div style={{ marginTop: "0.4vw", overflow: "hidden", height: "calc(100% - 3vw)" }}>
        {data.count === 0 && (
          <div style={{ fontSize: "0.75vw", color: "#666", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 800 }}>● Queue clear — standing by</div>
        )}
        {data.items.slice(0, 10).map((r) => (
          <div key={r.key} style={{ borderLeft: `3px solid ${STATUS_COLOR[r.status] || "#888"}`, padding: "0.2vw 0.5vw", marginBottom: "0.25vw", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.4vw" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.7vw", fontWeight: 900, color: STATUS_COLOR[r.status] || "#aaa", textTransform: "uppercase", letterSpacing: "0.05em" }}>{(r.status || "").replace(/_/g, " ")}</div>
              <div style={{ fontSize: "0.62vw", color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>→ {r.dropoff || "—"}</div>
            </div>
            <div style={{ fontSize: "0.6vw", color: "#666", fontVariantNumeric: "tabular-nums", fontWeight: 700 }}>
              {Math.floor((r.age_seconds || 0) / 60)}m
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Completed Rides · last-N ticker
// ─────────────────────────────────────────────────────────────────────
function CompletedRidesCell() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/completed-ticker?limit=10`).then((r) => setItems(r.data?.items || [])).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }}>
      <div style={{ fontSize: "0.7vw", color: "#888", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800 }}>Recently completed</div>
      <div style={{ marginTop: "0.3vw", overflow: "hidden", height: "calc(100% - 1.6vw)" }}>
        {items.length === 0 && (
          <div style={{ fontSize: "0.7vw", color: "#666", marginTop: "0.4vw" }}>No completed trips yet today.</div>
        )}
        {items.map((it) => (
          <div key={it.key} style={{ display: "flex", justifyContent: "space-between", padding: "0.18vw 0", borderBottom: "1px dashed #1a1a1f", gap: "0.4vw" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: "0.68vw", color: "#e4e4e7", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.dropoff || "—"}</div>
              <div style={{ fontSize: "0.55vw", color: "#666" }}>{it.miles ? `${it.miles} mi` : ""}</div>
            </div>
            <div style={{ fontSize: "0.78vw", color: "#fbbf24", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
              ${((it.total_cents || 0) / 100).toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Weather strip — bottom footer of the entire wall
// ─────────────────────────────────────────────────────────────────────
function WeatherStrip() {
  const [alerts, setAlerts] = useState(0);
  useEffect(() => {
    const load = () => axios.get("https://api.weather.gov/alerts/active?area=OK", { headers: { "User-Agent": "BullerTaxi Kiosk Wall" } }).then((r) => setAlerts((r.data?.features || []).length)).catch(() => {});
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="kiosk-weather-strip" style={{ flex: "0 0 auto", padding: "0.4vw 1vw", borderTop: "1px solid #222", fontSize: "0.7vw", color: "#888", display: "flex", justifyContent: "space-between", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800 }}>
      <span>{alerts > 0 ? <span style={{ color: "#ef4444" }}>● {alerts} NWS ALERT{alerts === 1 ? "" : "S"} ACTIVE</span> : <span style={{ color: "#22c55e" }}>● ALL CLEAR · OK</span>}</span>
      <span>Buller Taxi · Dispatch · v2.7.45</span>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════
// v2.7.60+E43 · VIDEO WALL OVERHAUL — new cell renderers
// ═════════════════════════════════════════════════════════════════════

// §5 · Active Weather Warnings · Top-10 threat queue with NOAA-standard
// color-coded FLASHING blocks (Tornado=red, Severe TStorm=yellow, Flash
// Flood=green). Polls the backend every 15s (backend fans out to NWS).
function WarningsTop10Cell() {
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => {
    const load = () => axios.get(`${API}/api/public/kiosk-wall/warnings-top10`)
      .then((r) => setData(r.data || { items: [], count: 0 })).catch(() => {});
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }} data-testid="warnings-top10-cell">
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.5vw", marginBottom: "0.3vw" }}>
        <div style={{ fontSize: "2vw", fontWeight: 900, color: data.count > 0 ? "#ef4444" : "#22c55e", fontVariantNumeric: "tabular-nums" }} data-testid="warnings-top10-count">{data.count}</div>
        <div style={{ fontSize: "0.7vw", color: "#888", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>Top-10 · OK NWS</div>
      </div>
      <div style={{ overflow: "hidden", height: "calc(100% - 3vw)" }}>
        {data.count === 0 && (
          <div style={{ fontSize: "0.75vw", color: "#22c55e", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 800 }}>● All Clear · No active OK warnings</div>
        )}
        {data.items.map((a) => (
          <div
            key={a.id}
            data-testid={`warning-item-${a.event?.replace(/\s+/g, "-").toLowerCase()}`}
            style={{
              background: a.color,
              color: "#fff",
              padding: "0.25vw 0.55vw",
              marginBottom: "0.2vw",
              borderRadius: "0.2vw",
              animation: a.flash ? "kioskWarnFlash 1s ease-in-out infinite" : undefined,
              boxShadow: a.flash ? `0 0 0.4vw ${a.color}` : undefined,
            }}
          >
            <div style={{ fontSize: "0.78vw", fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.05em", textShadow: "0 1px 0 rgba(0,0,0,0.6)" }}>{a.event}</div>
            <div style={{ fontSize: "0.62vw", opacity: 0.95, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.headline}</div>
          </div>
        ))}
      </div>
      {/* CSS keyframes for flash — scoped via a style tag so we don't
          pollute the global stylesheet. */}
      <style>{`@keyframes kioskWarnFlash{
        0%,100% { filter: brightness(1);   box-shadow: 0 0 0.3vw rgba(255,255,255,0.2); }
        50%     { filter: brightness(1.6); box-shadow: 0 0 1.2vw currentColor; }
      }`}</style>
    </div>
  );
}


// §3 · Fleet Status Stream — single-line-per-vehicle text board.
// Exact format enforced server-side; the cell just needs to word-wrap
// nicely and highlight status bucket coloring for scan-ability.
// §3 · Fleet Status Stream (v2.7.62+E45) — no longer zone-scoped since
// the global zone selector was removed; renders the ENTIRE fleet by
// default. To scope per-cell, set `cell.zone` on the wall config.
function FleetStreamCell({ cell }) {
  const { perf } = useKioskZone();
  const zone = cell?.zone || "";
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => {
    const load = () => {
      const url = `${API}/api/public/kiosk-wall/fleet-stream${zone ? `?zone=${encodeURIComponent(zone)}` : ""}`;
      axios.get(url).then((r) => setData(r.data || { items: [], count: 0 })).catch(() => {});
    };
    load();
    const id = setInterval(load, perf.fleetPollMs);
    return () => clearInterval(id);
  }, [zone, perf.fleetPollMs]);

  const STATUS_COLOR = {
    ONLINE: "#22c55e", "ON DUTY": "#22c55e", DISPATCHED: "#a78bfa",
    "EN ROUTE": "#22d3ee", "ON TRIP": "#facc15", BREAK: "#94a3b8", OFFLINE: "#64748b",
  };
  return (
    <div style={{ height: "100%", overflow: "hidden", padding: "0.4vw 0.8vw" }} data-testid="fleet-stream-cell">
      <div style={{ display: "flex", alignItems: "baseline", gap: "0.5vw", marginBottom: "0.3vw" }}>
        <div style={{ fontSize: "2vw", fontWeight: 900, color: "#22c55e", fontVariantNumeric: "tabular-nums" }} data-testid="fleet-stream-count">{data.count}</div>
        <div style={{ fontSize: "0.7vw", color: "#888", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>Fleet Stream {zone ? `· ${zone}` : ""}</div>
      </div>
      <div style={{ overflowY: "auto", overflowX: "hidden", height: "calc(100% - 3vw)", fontFamily: "'JetBrains Mono', 'Courier New', monospace" }}>
        {data.items.length === 0 && (
          <div style={{ fontSize: "0.75vw", color: "#666" }}>No drivers reporting.</div>
        )}
        {data.items.map((d, i) => (
          <div
            key={`${d.vehicle_id}-${i}`}
            data-testid={`fleet-line-${i}`}
            style={{
              fontSize: "0.72vw",
              padding: "0.15vw 0.3vw",
              borderLeft: `3px solid ${STATUS_COLOR[d.status] || "#475569"}`,
              marginBottom: "0.15vw",
              color: d.online ? "#e4e4e7" : "#71717a",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              letterSpacing: "0.02em",
            }}
          >{d.line}</div>
        ))}
      </div>
    </div>
  );
}


// §2 · 4-Column Dispatch Board — Active / Booked / Future(72h) / Completed(20)
// §2 · 4-Column Dispatch Board (v2.7.62+E45) — no longer zone-scoped
// globally. Renders org-wide dispatch by default; per-cell `cell.zone`
// still supported for isolated wall configs that want a zoned board.
// Variant modes (v2.7.62+E45):
//   • undefined / "all" → all four columns
//   • "abf"             → Active / Booked / Future 72h (3-col stack)
//   • "completed"       → Completed Last 20 only
// The QuadTopologyBody (§3) uses "abf" + "completed" variants to split
// the board into two separate cells for the 55/45 layout.
function DispatchColumnsCell({ cell }) {
  const { perf } = useKioskZone();
  const zone = cell?.zone || "";
  const variant = cell?.variant || "all";
  const [data, setData] = useState({ active: { items: [], count: 0 }, booked: { items: [], count: 0 }, future: { items: [], count: 0, window_hours: 72 }, completed: { items: [], count: 0 } });
  useEffect(() => {
    const load = () => {
      const url = `${API}/api/public/kiosk-wall/dispatch-columns${zone ? `?zone=${encodeURIComponent(zone)}` : ""}`;
      axios.get(url).then((r) => setData(r.data || data)).catch(() => {});
    };
    load();
    const id = setInterval(load, perf.dispatchPollMs);
    return () => clearInterval(id);
  }, [zone, perf.dispatchPollMs]);

  const COL_STYLE = { display: "flex", flexDirection: "column", background: "#0f0f12", border: "1px solid #1a1a1f", borderRadius: "0.3vw", overflow: "hidden", minWidth: 0 };
  const HEAD_STYLE = { flex: "0 0 auto", padding: "0.3vw 0.5vw", fontSize: "0.65vw", textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 800, borderBottom: "1px solid #1a1a1f", display: "flex", justifyContent: "space-between" };
  const BODY_STYLE = { flex: "1 1 auto", overflow: "hidden", padding: "0.25vw 0.4vw" };

  // v2.7.62+E45 · variant filtering. `abf` renders only Active/Booked/Future.
  // `completed` renders only the last 20 completed board (§3 quad topology).
  const showActive    = variant === "all" || variant === "abf";
  const showBooked    = variant === "all" || variant === "abf";
  const showFuture    = variant === "all" || variant === "abf";
  const showCompleted = variant === "all" || variant === "completed";
  const visibleCols = [showActive, showBooked, showFuture, showCompleted].filter(Boolean).length;

  return (
    <div style={{ height: "100%", display: "grid", gridTemplateColumns: `repeat(${visibleCols || 1}, 1fr)`, gap: "0.3vw", padding: "0.3vw" }} data-testid={`dispatch-columns-cell${variant !== "all" ? `-${variant}` : ""}`}>
      {showActive    && <DispatchColumn testid="dispatch-col-active"    title="Active"                    color="#22c55e" items={data.active.items}    count={data.active.count}    showEta       colStyle={COL_STYLE} headStyle={HEAD_STYLE} bodyStyle={BODY_STYLE}/>}
      {showBooked    && <DispatchColumn testid="dispatch-col-booked"    title="Booked / Dispatching"      color="#38bdf8" items={data.booked.items}    count={data.booked.count}    showEta       colStyle={COL_STYLE} headStyle={HEAD_STYLE} bodyStyle={BODY_STYLE}/>}
      {showFuture    && <DispatchColumn testid="dispatch-col-future"    title={`Future · ${data.future.window_hours || 72}h`} color="#a78bfa" items={data.future.items} count={data.future.count} showScheduled colStyle={COL_STYLE} headStyle={HEAD_STYLE} bodyStyle={BODY_STYLE}/>}
      {showCompleted && <DispatchColumn testid="dispatch-col-completed" title="Completed · Last 20"       color="#fbbf24" items={data.completed.items} count={data.completed.count} showFare      colStyle={COL_STYLE} headStyle={HEAD_STYLE} bodyStyle={BODY_STYLE}/>}
    </div>
  );
}

// v2.7.60+E43 · Extracted from DispatchColumnsCell to avoid the
// "components defined during render" antipattern (React would trash
// per-cell state every render otherwise).
function DispatchColumn({ title, color, items, count, testid, showEta, showScheduled, showFare, colStyle, headStyle, bodyStyle }) {
  return (
    <div style={colStyle} data-testid={testid}>
      <div style={{ ...headStyle, color }}>
        <span>{title}</span>
        <span style={{ color: "#fbbf24", fontVariantNumeric: "tabular-nums" }}>{count}</span>
      </div>
      <div style={bodyStyle}>
        {items.length === 0 && <div style={{ fontSize: "0.6vw", color: "#555" }}>—</div>}
        {items.map((r, i) => (
          <div key={r.full_id || i} style={{ padding: "0.15vw 0", borderBottom: "1px dashed #1a1a1f" }}>
            <div style={{ fontSize: "0.62vw", color: "#e4e4e7", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ color, fontWeight: 900, letterSpacing: "0.05em", marginRight: "0.4vw" }}>#{r.id}</span>
              <span style={{ color: "#94a3b8" }}>{r.status.replace(/_/g, " ").toUpperCase()}</span>
              {r.vehicle && r.vehicle !== "—" && <span style={{ color: "#a78bfa", marginLeft: "0.4vw" }}>· {r.vehicle}</span>}
            </div>
            <div style={{ fontSize: "0.58vw", color: "#71717a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {r.pickup ? `${r.pickup} → ` : "→ "}{r.dropoff || "—"}
            </div>
            <div style={{ fontSize: "0.55vw", color: "#4b5563", display: "flex", gap: "0.4vw" }}>
              {showEta && r.eta_minutes != null && <span>ETA {r.eta_minutes}m</span>}
              {showScheduled && r.scheduled_at && <span>{new Date(r.scheduled_at).toLocaleString([], { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
              {showFare && (r.fare > 0 || r.miles > 0) && (
                <>
                  {r.fare > 0 && <span style={{ color: "#fbbf24" }}>${r.fare.toFixed(2)}</span>}
                  {r.miles > 0 && <span>{r.miles.toFixed(1)}mi</span>}
                  {r.closure && <span style={{ color: "#22c55e" }}>{r.closure}</span>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// §1 · Zoned Live Map — v2.7.62+E45 REFACTOR.
//   • REMOVED all jurisdiction grid rendering (A1..F6 lines + labels,
//     bounding-box rectangle, "Show Jurisdiction Grid" toggle) per the
//     new spec: "Fully remove all jurisdiction grids, alphanumeric
//     overlay grids, and related toggles from the application entirely."
//   • ADDED per-cell zone dropdown — every ZonedLiveMap instance now
//     has its own independent zone selector at the top-left. Changing
//     one map's zone does NOT affect any other map on the wall.
//   • Multi-instance safe — up to 7 concurrent map cells share the
//     same driver polling contract but each maintains its own local
//     zone state, marker layer, and Google Maps instance.
//
// Every map still renders:
//   - Landmark markers (curated per-zone list — visual reference points)
//   - Red center marker (dispatch core of that zone)
//   - Color-coded live driver markers with smooth interpolation
function ZonedLiveMapCell({ cell }) {
  const { zones, perf } = useKioskZone();
  const ref = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);
  const driverMarkersRef = useRef(new Map());
  const animRafRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [driverStats, setDriverStats] = useState({ count: 0, buckets: {} });

  // Per-cell zone selection — initialized from the cell's config, else
  // the first known zone. NO cross-cell propagation.
  const initialZone = cell?.zone && zones.find((z) => z.id === cell.zone)
    ? cell.zone
    : zones[0]?.id || "";
  const [localZone, setLocalZone] = useState(initialZone);

  // Keep localZone in sync if `zones` loads after cell mount (first render
  // may see zones=[] so initialZone falls back to "").
  useEffect(() => {
    if (!localZone && zones.length > 0) {
      setLocalZone((cell?.zone && zones.find((z) => z.id === cell.zone)) ? cell.zone : zones[0].id);
    }
  }, [zones, cell, localZone]);

  const activeZone = zones.find((z) => z.id === localZone) || zones[0] || null;

  // Initialise the map ONCE, reuse across zone changes for smoothness.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { default: loadGoogleMaps } = await import("@/lib/loadGoogleMaps");
        await loadGoogleMaps();
        if (cancelled || !ref.current || !window.google) return;
        mapRef.current = new window.google.maps.Map(ref.current, {
          center: activeZone?.center || { lat: 35.4676, lng: -97.5164 },
          zoom: activeZone?.zoom || 10,
          disableDefaultUI: false,
          gestureHandling: "greedy",
          zoomControl: true,
          zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
          scaleControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          rotateControl: false,
          backgroundColor: "#000",
          styles: DARK_MAP_STYLES,
          maxZoom: perf.mapMaxZoom,
          minZoom: 4,
        });
        setLoaded(true);
      } catch (e) {
        console.warn("[ZonedLiveMapCell] init failed", e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Whenever THIS cell's zone changes → pan + rebuild landmarks / center marker.
  // (Grid overlays are gone; only landmarks + core dispatch pin remain.)
  useEffect(() => {
    if (!loaded) return;
    const map = mapRef.current;
    if (!map || !activeZone || !window.google) return;
    overlaysRef.current.forEach((o) => o.setMap(null));
    overlaysRef.current = [];

    map.panTo(activeZone.center);
    map.setZoom(activeZone.zoom || 12);

    // Landmark markers (blue dots)
    (activeZone.landmarks || []).forEach((lm) => {
      overlaysRef.current.push(new window.google.maps.Marker({
        map,
        position: { lat: lm.lat, lng: lm.lng },
        label: { text: lm.label, color: "#e2e8f0", fontSize: "9px", fontWeight: "700" },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 4, fillColor: "#38bdf8", fillOpacity: 1,
          strokeColor: "#0c4a6e", strokeWeight: 1.5,
        },
        clickable: false,
      }));
    });

    // Core dispatch center marker (red dot)
    overlaysRef.current.push(new window.google.maps.Marker({
      map,
      position: activeZone.center,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8, fillColor: "#ef4444", fillOpacity: 1,
        strokeColor: "#000", strokeWeight: 2,
      },
      clickable: false,
    }));
  }, [activeZone, loaded]);

  // ─────────────────────────────────────────────────────────────────
  // Live driver GPS telemetry — filtered to THIS cell's zone bbox.
  // ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!loaded || !mapRef.current || !window.google) return undefined;
    const g = window.google;
    const pollMs = Math.max(2000, Math.min(3500, perf.dispatchPollMs / 4 || 2500));
    let cancelled = false;

    const applyUpdate = (items) => {
      const map = mapRef.current;
      const seen = new Set();
      const now = Date.now();
      items.forEach((d) => {
        seen.add(d.id);
        const existing = driverMarkersRef.current.get(d.id);
        if (existing) {
          existing.prevLat = existing.animLat;
          existing.prevLng = existing.animLng;
          existing.targetLat = d.lat;
          existing.targetLng = d.lng;
          existing.updateStart = now;
          existing.updateDuration = pollMs;
          const icon = existing.marker.getIcon();
          if (icon && typeof icon === "object" && icon.fillColor !== d.status_color) {
            existing.marker.setIcon({ ...icon, fillColor: d.status_color, strokeColor: d.status_color });
          }
        } else {
          const marker = new g.maps.Marker({
            map,
            position: { lat: d.lat, lng: d.lng },
            zIndex: 1000,
            title: `${d.name || d.vehicle_id} · ${d.status_raw}`,
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              scale: 7,
              fillColor: d.status_color,
              fillOpacity: 0.95,
              strokeColor: d.status_color,
              strokeWeight: 2.5,
              strokeOpacity: 0.6,
            },
            optimized: false,
            label: { text: (d.vehicle_id || d.name || "").toString().slice(0, 5), color: "#000", fontSize: "9px", fontWeight: "900" },
          });
          driverMarkersRef.current.set(d.id, {
            marker,
            animLat: d.lat, animLng: d.lng,
            prevLat: d.lat, prevLng: d.lng,
            targetLat: d.lat, targetLng: d.lng,
            updateStart: now, updateDuration: pollMs,
          });
        }
      });
      Array.from(driverMarkersRef.current.entries()).forEach(([id, obj]) => {
        if (!seen.has(id)) {
          obj.marker.setMap(null);
          driverMarkersRef.current.delete(id);
        }
      });
      const buckets = { available: 0, en_route: 0, loaded: 0, offline: 0 };
      items.forEach((d) => { buckets[d.status_bucket] = (buckets[d.status_bucket] || 0) + 1; });
      setDriverStats({ count: items.length, buckets });
    };

    const load = () => {
      const url = `${API}/api/public/kiosk-wall/driver-positions${localZone ? `?zone=${encodeURIComponent(localZone)}` : ""}`;
      axios.get(url).then((r) => { if (!cancelled) applyUpdate(r.data?.items || []); }).catch(() => {});
    };
    load();
    const pollId = setInterval(load, pollMs);

    const tick = () => {
      const now = Date.now();
      driverMarkersRef.current.forEach((obj) => {
        const t = Math.min(1, (now - obj.updateStart) / obj.updateDuration);
        const eased = 1 - Math.pow(1 - t, 3);
        obj.animLat = obj.prevLat + (obj.targetLat - obj.prevLat) * eased;
        obj.animLng = obj.prevLng + (obj.targetLng - obj.prevLng) * eased;
        obj.marker.setPosition({ lat: obj.animLat, lng: obj.animLng });
      });
      animRafRef.current = requestAnimationFrame(tick);
    };
    animRafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      clearInterval(pollId);
      if (animRafRef.current) cancelAnimationFrame(animRafRef.current);
      driverMarkersRef.current.forEach((o) => o.marker.setMap(null));
      driverMarkersRef.current.clear();
    };
  }, [loaded, localZone, perf.dispatchPollMs]);

  return (
    <div style={{ position: "relative", height: "100%" }} data-testid="zoned-live-map-cell">
      <div ref={ref} style={{ position: "absolute", inset: 0 }} data-testid="zoned-live-map"/>
      {!loaded && (
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#888", fontSize: "0.7vw", textTransform: "uppercase", letterSpacing: "0.1em" }}>Loading regional map…</div>
      )}

      {/* Per-cell zone selector (§2 v2.7.62+E45) */}
      <div
        style={{
          position: "absolute", left: "0.4vw", top: "0.4vw",
          padding: "0.25vw 0.5vw", background: "rgba(0,0,0,0.82)",
          borderRadius: "0.25vw", border: "1px solid rgba(251,191,36,0.35)",
          display: "flex", alignItems: "center", gap: "0.4vw",
        }}
      >
        <span style={{ fontSize: "0.55vw", color: "#94a3b8", letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 800 }}>Zone</span>
        <select
          data-testid={`per-cell-zone-select-${cell?.id || cell?.slot || "x"}`}
          value={localZone}
          onChange={(e) => setLocalZone(e.target.value)}
          style={{
            background: "#111827", color: "#fbbf24", border: "1px solid #374151",
            padding: "0.15vw 0.4vw", fontSize: "0.68vw", fontWeight: 800,
            letterSpacing: "0.03em", textTransform: "uppercase", borderRadius: "0.2vw",
            fontFamily: "inherit", cursor: "pointer",
          }}
        >
          {zones.map((z) => (
            <option key={z.id} value={z.id}>{z.id} · {z.name}</option>
          ))}
        </select>
      </div>

      {/* Live driver legend */}
      <div
        data-testid="driver-legend"
        style={{
          position: "absolute", left: "0.4vw", bottom: "0.4vw",
          padding: "0.3vw 0.5vw", background: "rgba(0,0,0,0.82)", borderRadius: "0.25vw",
          fontSize: "0.55vw", color: "#e4e4e7", letterSpacing: "0.06em",
          display: "flex", gap: "0.5vw", alignItems: "center",
        }}
      >
        <span style={{ color: "#94a3b8", fontWeight: 800, textTransform: "uppercase" }}>
          Fleet · <span data-testid="driver-legend-total" style={{ color: "#fbbf24" }}>{driverStats.count}</span>
        </span>
        <LegendDot color="#22c55e" count={driverStats.buckets.available || 0} label="Avail"    testid="legend-available"/>
        <LegendDot color="#f59e0b" count={driverStats.buckets.en_route  || 0} label="EnRoute"  testid="legend-en-route"/>
        <LegendDot color="#ef4444" count={driverStats.buckets.loaded    || 0} label="Loaded"   testid="legend-loaded"/>
        <LegendDot color="#6b7280" count={driverStats.buckets.offline   || 0} label="Offline"  testid="legend-offline"/>
      </div>
    </div>
  );
}

function LegendDot({ color, count, label, testid }) {
  return (
    <span data-testid={testid} style={{ display: "inline-flex", alignItems: "center", gap: "0.2vw" }}>
      <span style={{ width: "0.5vw", height: "0.5vw", borderRadius: "50%", background: color, boxShadow: `0 0 0.25vw ${color}` }}/>
      <span style={{ color: "#e4e4e7" }}>{count} <span style={{ color: "#71717a" }}>{label}</span></span>
    </span>
  );
}

